import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { CompanyDto } from "model/Company";
import { isSettingEnabled } from "model/settings.logic";
import { useAuthStore } from "stores/authStore/authStore";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { SimflexityPayTerms } from "./simflexityPayTerms";
import {
  FormRow,
  FormTitle,
  GigpayWrapper,
  LabelStyled,
  ReadOnlyStyled,
  SimflexityPayErrorText,
  SimflexityPayStrongText,
  SimflexityPayText,
  SimflexityPayTitle,
  TermsContainer,
  TermsLink,
  TermsReadText,
} from "./simflexityPay.styled";

type PropsTypes = {
  companyId: string;
  variant?: "default" | "settings";
  showTitle?: boolean;
};

export const SimflexityPayCompany = (props: PropsTypes): JSX.Element => {
  const [companyState, , companyDispatch] = useCompanyStore();
  const [authState] = useAuthStore();
  const [showTerms, setShowTerms] = useState(false);
  const [readTerms, setReadTerms] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [connected, setConnected] = useState(false);
  const [company, setCompany] = useState<CompanyDto | undefined>(undefined);
  const [simflexityPayInfoSent, setSimflexityPayInfoSent] = useState(false);
  const [simflexityPayConnected, setSimflexityPayConnected] = useState(false);
  const { t } = useTranslation();

  const companyId = props.companyId;
  const variant = props.variant ?? "default";
  const showTitle = props.showTitle ?? true;

  useEffect(() => {
    const companySettings = companyState.company?.companySettings;
    setSimflexityPayInfoSent(
      isSettingEnabled(companySettings, "EnableSimflexityPay"),
    );
    setSimflexityPayConnected(
      isSettingEnabled(companySettings, "SimflexityPay"),
    );
  }, [companyState.company]);

  useEffect(() => {
    const existingCompany = companyState.companies.find(
      (currentCompany) => currentCompany.id === Number(companyId),
    );
    setCompany(existingCompany);
  }, [companyId, companyState.companies]);

  const userIsCompanyUserForCompany = (currentCompanyId: string): boolean => {
    return companyState.companies.some(
      (currentCompany) => currentCompany.id === Number(currentCompanyId),
    );
  };

  const setupSimflexityPay = (): void => {
    setErrorText("");
    if (!company) return;

    setButtonLoading(true);
    Api()
      .company.setupSimflexityPayForCompany(company.id)
      .then((res) => {
        if (res.data.error) {
          setErrorText(res.data.error.message);
          return;
        }

        setConnected(true);
        if (companyState.company?.companySettings) {
          companyDispatch({
            type: "UPDATE_COMPANY_INFORMATION",
            payload: {
              ...companyState.company,
              companySettings: [
                ...companyState.company.companySettings,
                {
                  companyId: companyState.company.id,
                  key: "SimflexityPay",
                  value: "true",
                },
              ],
            },
          });
        } else if (companyState.company) {
          companyDispatch({
            type: "UPDATE_COMPANY_INFORMATION",
            payload: {
              ...companyState.company,
              companySettings: [
                {
                  companyId: companyState.company.id,
                  key: "SimflexityPay",
                  value: "true",
                },
              ],
            },
          });
        }
      })
      .finally(() => {
        setButtonLoading(false);
      });
  };

  const renderOops = (): JSX.Element => {
    return (
      <>
        <SimflexityPayStrongText $variant={variant}>
          {t("General.Oops")}
        </SimflexityPayStrongText>
        <SimflexityPayText $variant={variant}>
          {t("General.WrongPlace")}
        </SimflexityPayText>
      </>
    );
  };

  const renderNotConnected = (): JSX.Element => {
    return (
      <div>
        <SimflexityPayStrongText $variant={variant}>
          {t("SimflexityPay.ThisCompanyIsNotConnected")}
        </SimflexityPayStrongText>
        <SimflexityPayText $variant={variant}>
          {t("SimflexityPay.ContactSimflexityForMore")}
        </SimflexityPayText>
      </div>
    );
  };

  const renderConnectForm = (): JSX.Element => {
    return (
      <>
        <FormTitle $variant={variant}>
          {t("ProfileCompany.CompanyDetails")}
        </FormTitle>
        <FormRow>
          <LabelStyled>{t("RegisterCompany.LegalName")}</LabelStyled>
          <ReadOnlyStyled>{company?.legalName}</ReadOnlyStyled>
        </FormRow>
        <FormRow>
          <LabelStyled>{t("RegisterCompany.RegistrationNumber")}</LabelStyled>
          <ReadOnlyStyled>{company?.registrationNumber}</ReadOnlyStyled>
        </FormRow>
        <FormRow>
          <LabelStyled>{t("LoginCompany.Email")}</LabelStyled>
          <ReadOnlyStyled>{company?.email}</ReadOnlyStyled>
        </FormRow>
        <FormRow>
          <LabelStyled>{t("RegisterCompany.ContactPerson")}</LabelStyled>
          <ReadOnlyStyled>{company?.contactPerson}</ReadOnlyStyled>
        </FormRow>
        <FormRow>
          <LabelStyled>{t("RegisterCompany.CompanyAddress")}</LabelStyled>
          <ReadOnlyStyled>{company?.address}</ReadOnlyStyled>
        </FormRow>
        <FormRow>
          <LabelStyled>{t("RegisterCompany.PostalCode")}</LabelStyled>
          <ReadOnlyStyled>{company?.postalCode}</ReadOnlyStyled>
        </FormRow>
        <FormRow>
          <LabelStyled>{t("RegisterCompany.City")}</LabelStyled>
          <ReadOnlyStyled>{company?.city}</ReadOnlyStyled>
        </FormRow>
        {!simflexityPayConnected && (
          <>
            <TermsLink $variant={variant} onClick={() => setShowTerms(true)}>
              {t("SimflexityPay.Terms")}
            </TermsLink>
            <TermsContainer style={{ display: "flex" }}>
              <Checkbox
                checked={readTerms ? "checked" : "unchecked"}
                onChange={setReadTerms}
              />
              <TermsReadText $variant={variant}>
                {t("SimflexityPay.ReadConditions")}
              </TermsReadText>
            </TermsContainer>
            <PrimaryButton
              disabled={!readTerms || buttonLoading}
              onClick={setupSimflexityPay}
              label={t("SimflexityPay.ConnectToSimflexity")}
              size="small"
              isLoading={buttonLoading}
            />
            <SimflexityPayErrorText>{errorText}</SimflexityPayErrorText>
          </>
        )}
      </>
    );
  };

  const renderConnected = (): JSX.Element => {
    return (
      <>
        <SimflexityPayStrongText $variant={variant}>
          {t("SimflexityPay.Connected")}
        </SimflexityPayStrongText>
        <SimflexityPayText $variant={variant}>
          {company?.companyName} {t("SimflexityPay.IsConnectedTo")}
        </SimflexityPayText>
      </>
    );
  };

  return (
    <>
      {showTitle ? (
        <SimflexityPayTitle $variant={variant}>
          {t("CompanyUsers.Payments")}
        </SimflexityPayTitle>
      ) : null}

      <GigpayWrapper>
        {!connected &&
          !simflexityPayConnected &&
          userIsCompanyUserForCompany(companyId) &&
          simflexityPayInfoSent &&
          renderConnectForm()}

        {(connected || simflexityPayConnected) &&
          userIsCompanyUserForCompany(companyId) &&
          renderConnected()}

        {authState.signedIn && !userIsCompanyUserForCompany(companyId)
          ? renderOops()
          : null}

        {!connected &&
          !simflexityPayConnected &&
          !simflexityPayInfoSent &&
          renderNotConnected()}
      </GigpayWrapper>

      <SimflexityPayTerms setShowTerms={setShowTerms} showTerms={showTerms} />
    </>
  );
};
